import { planSponsoredInsertions } from '../ads/placement';

// Keep the raw API paging fixed. Display windows consume a deduplicated stream,
// so filling a wide row never steals albums from or repeats them on the next page.
export function albumWindowSize(columns, campaigns, minimum = 20) {
  const native = campaigns.filter(
    (c) => c.placements?.[0]?.format === 'NATIVE_ALBUM' && c.albums?.length,
  );
  const ads = planSponsoredInsertions(native, minimum).length;
  return minimum + ((columns - ((minimum + ads) % columns)) % columns);
}
export async function loadAlbumWindow(args, signal, load, seed) {
  // This endpoint already removes version-group duplicates and advertised albums.
  // Request its exact display window so jumping to the final page is one request,
  // rather than downloading every intervening raw page.
  if (args.category === 'releases' && args.sort === 'recommended') {
    const result = await load('albums', { ...args, size: args.windowSize }, signal);
    const totalPages = Math.max(1, Number(result.pagination?.totalPages));
    if (!Number.isSafeInteger(totalPages)) throw new Error('Invalid album pagination');
    if (args.page > totalPages) return loadAlbumWindow({ ...args, page: totalPages }, signal, load);
    return result;
  }
  const pages = seed?.pages ? [...seed.pages] : [];
  let complete = seed?.complete || false;
  const excluded = new Set((args.windowExcludedIds || []).map(String));
  const select = () => {
    const seen = new Set();
    return pages
      .flatMap((p) => p.albums)
      .filter((item) => {
        if (item?.id == null) return false;
        const id = String(item.id);
        if (seen.has(id) || excluded.has(id)) return false;
        seen.add(id);
        return true;
      });
  };
  let items = select();
  const size = args.windowSize;
  const needed = args.page * size;
  // The API is a unique, ordered album stream and supplies its complete count.
  // Advertising is already excluded by the recommended-release endpoint. Other
  // sorts hide matching ad IDs locally: only read beyond the window if an unseen
  // ad could change the last page number, never advertise a guessed page count.
  const bounds = () => {
    if (complete) return [items.length, items.length];
    const rawTotal = pages[0]?.pagination?.totalItems;
    if (!Number.isSafeInteger(rawTotal) || rawTotal < 0) return [null, null];
    const seenIds = new Set(pages.flatMap((p) => p.albums).map((item) => String(item?.id)));
    const rawRead = pages.reduce((n, p) => n + p.albums.length, 0);
    const removed = rawRead - items.length;
    const unseenAds = [...excluded].filter((id) => !seenIds.has(id)).length;
    const upper = Math.max(items.length, rawTotal - removed);
    return [Math.max(items.length, upper - unseenAds), upper];
  };
  const pageCount = (count) => (count == null ? null : Math.max(1, Math.ceil(count / size)));
  const stableCount = () => {
    const [lower, upper] = bounds();
    return lower != null && pageCount(lower) === pageCount(upper);
  };
  while (!complete && (items.length < needed || !stableCount())) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const page = pages.length + 1;
    const result = await load('albums', { ...args, page, size: 20 }, signal);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (Number(result.pagination?.currentPage) !== page) throw new Error('Unexpected album page');
    const total = Number(result.pagination?.totalPages);
    if (!Number.isSafeInteger(total) || total < 0) throw new Error('Invalid album pagination');
    pages.push(result);
    complete = page >= total;
    items = select();
  }
  const [lower, upper] = bounds();
  const totalPages = pageCount(upper);
  const currentPage = Math.min(args.page, totalPages);
  return {
    albums: items.slice((currentPage - 1) * size, currentPage * size),
    pagination: { currentPage, totalPages, totalItems: lower === upper ? upper : null },
    source: { pages, complete },
  };
}
